import { Brain, Database } from "lucide-react";
import { STATUS_META, StatusPill, fmtPct } from "./agentShared";

const BrainNode = ({ brain }) => (
  <div className="relative mx-auto w-full max-w-md rounded-2xl border border-primary/40 bg-card p-5 shadow-[0_0_0_6px_hsl(var(--primary)/0.06)]" data-testid="brain-node">
    <div className="flex items-center gap-3">
      <span className="relative flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
        <span className="absolute inset-0 rounded-full border border-primary/40 animate-ping opacity-40" />
        <Brain className="h-5 w-5 text-primary" strokeWidth={1.5} />
      </span>
      <div className="min-w-0">
        <p className="font-heading text-lg font-semibold tracking-tight" data-testid="brain-node-name">{brain.name}</p>
        <p className="text-xs text-muted-foreground font-mono-tech truncate">{brain.version}</p>
      </div>
      <StatusPill status={brain.status} testId="brain-node-status" />
    </div>
    <div className="mt-4 grid grid-cols-3 gap-2 text-center">
      {[["Tasks routed", brain.tasks_total, "brain-kpi-tasks"], ["Memory hits", brain.memory_hits, "brain-kpi-hits"], ["Hit rate", fmtPct(brain.memory_hit_rate), "brain-kpi-rate"],
        ["Collapsed", brain.collapsed_requests, "brain-kpi-collapsed"], ["Gemini calls avoided", brain.gemini_calls_avoided, "brain-kpi-gemini-avoided"], ["In flight", brain.in_flight, "brain-kpi-inflight"]].map(([l, v, t]) => (
        <div key={t} className="rounded-lg bg-muted/40 py-2">
          <p className="font-mono-tech text-base font-bold" data-testid={t}>{v ?? 0}</p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</p>
        </div>
      ))}
    </div>
  </div>
);

const AgentNode = ({ agent, onOpen }) => {
  const m = STATUS_META[agent.status] || STATUS_META.IDLE;
  return (
    <div className="flex w-full flex-col items-center" data-testid={`agent-column-${agent.agent_id}`}>
      <span className="h-6 w-px bg-border" />
      <button type="button" onClick={() => onOpen(agent.agent_id)} data-testid={`agent-node-${agent.agent_id}`}
        className={`group w-full rounded-xl border bg-card p-3 text-left transition-[transform,border-color,box-shadow] duration-200 hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${agent.status === "ERROR" ? "border-red-500/40" : agent.status === "DISABLED" ? "border-dashed border-border opacity-70" : "border-border"}`}>
        <div className="flex items-start justify-between gap-2">
          <span className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${m.dot}`} data-testid={`agent-status-dot-${agent.agent_id}`} title={agent.status_reason} />
          <span className="text-[10px] font-mono-tech uppercase tracking-wider text-muted-foreground" data-testid={`agent-status-${agent.agent_id}`}>{m.label}</span>
        </div>
        <p className="mt-1 text-sm font-semibold font-heading leading-tight" data-testid={`agent-name-${agent.agent_id}`}>{agent.name}</p>
        <p className="mt-0.5 text-[11px] text-muted-foreground leading-snug line-clamp-2">{agent.role}</p>
        <p className="mt-2 text-[11px] font-mono-tech text-muted-foreground" data-testid={`agent-tasks-${agent.agent_id}`}>{agent.performance.total_tasks} task{agent.performance.total_tasks === 1 ? "" : "s"}</p>
      </button>
      <span className="h-4 w-px bg-border" />
      <div className="flex w-full items-center gap-1.5 rounded-lg border border-dashed border-border bg-muted/30 px-2 py-1.5 text-[11px]" data-testid={`agent-memory-${agent.agent_id}`}>
        <Database className="h-3 w-3 text-primary shrink-0" strokeWidth={1.5} />
        <span className="truncate">Memory · {agent.memory.entries} · hit {fmtPct(agent.performance.memory_hit_rate)}</span>
      </div>
    </div>
  );
};

export const AgentGraph = ({ brain, agents, onOpen }) => (
  <div data-testid="agent-graph">
    <BrainNode brain={brain} />
    <div className="mx-auto h-8 w-px bg-border" />
    <div className="relative">
      <div className="absolute left-[10%] right-[10%] top-0 h-px bg-border" />
      <div className="grid grid-cols-2 gap-x-3 gap-y-6 md:grid-cols-3 lg:grid-cols-5" data-testid="agent-grid">
        {agents.map((a) => <AgentNode key={a.agent_id} agent={a} onOpen={onOpen} />)}
      </div>
    </div>
  </div>
);
